<script setup lang="ts">
import { computed } from 'vue';
import { ChevronRightIcon } from '@heroicons/vue/24/outline';
import ProviderBadge from './ProviderBadge.vue';
import HitTypeTag from './HitTypeTag.vue';
import {
  AGE_TONE_CLASSES,
  ageInQueueLabel,
  ageInQueueTone,
  ageInQueueTooltip,
} from '@/util/ageInQueue';
import { initials, avatarHue } from '@/util/avatar';
import type { QueueRowData } from './queueRow';

const props = defineProps<{ row: QueueRowData }>();

const age = computed(() => ageInQueueLabel(props.row.createdAt));
const ageTone = computed(() => ageInQueueTone(props.row.createdAt));
const ageTooltip = computed(() => ageInQueueTooltip(props.row.createdAt));
const hue = computed(() => avatarHue(props.row.name));
const hitsSummary = computed(() => {
  const { hitsTotal, hitsOpen } = props.row;
  return `${hitsTotal} ${hitsTotal === 1 ? 'hit' : 'hits'} · ${hitsOpen} open`;
});
</script>

<template>
  <div class="group relative col-span-4 grid grid-cols-subgrid lg:col-span-6 xl:col-span-7">
    <RouterLink
      :to="`/aml-review/${row.id}`"
      data-test="queue-row"
      :aria-label="`Review ${row.name}`"
      class="absolute inset-0 z-10 rounded-sm focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none focus-visible:ring-inset"
    />
    <div
      class="flex min-w-0 items-center gap-3 border-b border-gray-100 px-4 py-3 group-hover:bg-gray-50"
    >
      <div
        class="flex h-9 w-9 flex-none items-center justify-center rounded-full text-xs font-bold"
        :style="{ backgroundColor: `hsl(${hue} 90% 94%)`, color: `hsl(${hue} 60% 32%)` }"
      >
        {{ initials(row.name) }}
      </div>
      <div class="flex min-w-0 flex-col justify-center gap-0.5">
        <span class="truncate text-sm font-medium text-gray-900">{{ row.name }}</span>
        <span class="truncate font-mono text-xs text-gray-400">{{ row.externalId }}</span>
      </div>
    </div>
    <div
      class="flex items-center border-b border-gray-100 px-4 py-3 text-sm text-gray-700 group-hover:bg-gray-50"
    >
      {{ row.partner }}
    </div>
    <div
      class="hidden items-center border-b border-gray-100 px-4 py-3 group-hover:bg-gray-50 lg:flex"
    >
      <ProviderBadge :provider="row.provider" />
    </div>
    <div
      class="hidden items-center border-b border-gray-100 px-4 py-3 text-sm text-gray-700 group-hover:bg-gray-50 xl:flex"
    >
      {{ row.region }}
    </div>
    <div
      class="flex min-w-0 flex-col justify-center gap-1 border-b border-gray-100 px-4 py-3 group-hover:bg-gray-50"
    >
      <div class="flex flex-wrap items-center gap-1">
        <HitTypeTag v-for="hitType in row.hitTypes" :key="hitType" :hit-type="hitType" />
      </div>
      <span class="text-xs text-gray-400">{{ hitsSummary }}</span>
    </div>
    <div
      class="flex items-center justify-center border-b border-gray-100 px-4 py-3 group-hover:bg-gray-50"
    >
      <span class="group/age relative inline-flex">
        <RouterLink
          :to="`/aml-review/${row.id}`"
          :aria-label="ageTooltip"
          tabindex="-1"
          class="relative z-20 inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
          :class="AGE_TONE_CLASSES[ageTone]"
        >
          {{ age }}
        </RouterLink>
        <span
          role="tooltip"
          class="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1 -translate-x-1/2 rounded-md bg-gray-900 px-2 py-1 text-[11px] whitespace-nowrap text-white opacity-0 transition-opacity duration-100 group-hover/age:opacity-100"
        >
          {{ ageTooltip }}
          <span
            class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"
          ></span>
        </span>
      </span>
    </div>
    <div
      class="hidden items-center justify-center border-b border-gray-100 px-2 py-3 text-gray-400 group-hover:bg-gray-50 lg:flex"
    >
      <ChevronRightIcon class="size-4" aria-hidden="true" />
    </div>
  </div>
</template>
